"use client";

import React, { useState, memo, useEffect, useRef } from "react";
import { Box, Container, Typography, Button, Stack, Chip } from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import "../app/globals.css";
import { IMAGES } from "../constants/images";

const FourEasySteps = memo(() => {
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const [selectedStep, setSelectedStep] = useState(0);
  const containerRef = useRef(null);
  const stepsRef = useRef([]);

  const steps = [
    {
      number: t("fourEasySteps.steps.0.number"),
      title: t("fourEasySteps.steps.0.title"),
      description: t("fourEasySteps.steps.0.description"),
      image: IMAGES.FOUR_STEP_1_IMAGE,
      imageAlt:
        "Setup AI recruiter in 7 minutes with AI recruiting tool configuration",
      color: "#6DD130",
      bgColor: "rgba(109, 209, 48, 0.1)",
      borderColor: "rgba(109, 209, 48, 0.3)",
    },
    {
      number: t("fourEasySteps.steps.1.number"),
      title: t("fourEasySteps.steps.1.title"),
      description: t("fourEasySteps.steps.1.description"),
      image: IMAGES.FOUR_STEP_2_IMAGE,
      imageAlt:
        "AI interview activation with video interview software in 24 hours",
      color: "#6CCDEE",
      bgColor: "rgba(108, 205, 238, 0.1)",
      borderColor: "rgba(108, 205, 238, 0.3)",
    },
    {
      number: t("fourEasySteps.steps.2.number"),
      title: t("fourEasySteps.steps.2.title"),
      description: t("fourEasySteps.steps.2.description"),
      image: IMAGES.FOUR_STEP_3_IMAGE,
      imageAlt:
        "Review AI-generated reports from AI recruiting software in 48 hours",
      color: "#9048FB",
      bgColor: "rgba(144, 72, 251, 0.1)",
      borderColor: "rgba(144, 72, 251, 0.3)",
    },
    {
      number: t("fourEasySteps.steps.3.number"),
      title: t("fourEasySteps.steps.3.title"),
      description: t("fourEasySteps.steps.3.description"),
      image: IMAGES.FOUR_STEP_4_IMAGE,
      imageAlt:
        "Make hire decision in 72 hours using AI recruiting tool insights",
      color: "#FFB200",
      bgColor: "rgba(255, 178, 0, 0.1)",
      borderColor: "rgba(255, 178, 0, 0.3)",
    },
  ];

  // Scroll-based step activation
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const viewportCenter = window.innerHeight / 2;

      stepsRef.current.forEach((stepElement, index) => {
        if (!stepElement) return;

        const stepRect = stepElement.getBoundingClientRect();
        const stepCenter = stepRect.top + stepRect.height / 2;
        const distanceFromCenter = Math.abs(stepCenter - viewportCenter);

        // Activate step closest to viewport center
        if (distanceFromCenter < stepRect.height) {
          setSelectedStep(index);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 4, sm: 5, md: 8 },
        mt: { xs: 0, sm: 0, md: "-4%" },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3, md: 3 },
        }}
      >
        {/* Chip */}
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "center" },
          }}
        >
          <Chip
            label={t("fourEasySteps.chip")}
            sx={{
              backgroundColor: "transparent",
              border: "1px solid #DDE4E8",
              mb: { xs: 2, sm: 2.5, md: 1 },
              color: "#647482",
              fontSize: { xs: "12px", sm: "14px", md: "16px" },
              fontWeight: "400",
              textTransform: "capitalize",
              px: { xs: 0.5, sm: 0.8 },
              py: { xs: 1.2, sm: 1.5, md: 2.4 },
              fontFamily: "var(--font-outfit)",
              borderRadius: "40px",
              textAlign: "center",
              ml: { sm: 1, md: 0, lg: 0 },
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontSize: {
              xs: "1.5rem",
              sm: "1.8rem",
              md: "2rem",
              lg: "2rem",
              xl: "2.7rem",
            },
            fontWeight: "bold",
            mb: { xs: 2, md: 3 },
            fontFamily: "var(--font-plusjakarta)",
            textAlign: { xs: isArabic ? "right" : "left", md: "center" },
            mt: { xs: 3, md: 0, lg: "1.3%" },
            maxWidth: 800,
            color: "#000",
            mx: "auto",
          }}
        >
          {t("fourEasySteps.heading1")}{" "}
          <span style={{ color: "#6002EB" }}>
            {t("fourEasySteps.headingHighlight")}
          </span>{" "}
          {t("fourEasySteps.heading2")}
        </Typography>

        {/* Layout */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: { xs: 4, md: 10 },
            mt: "4%",
          }}
        >
          {/* Step List */}
          <Box sx={{ flex: 1, maxWidth: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {steps.map((step, index) => {
                const isSelected = selectedStep === index;

                return (
                  <motion.div
                    key={index}
                    ref={(el) => (stepsRef.current[index] = el)}
                    initial={{ opacity: 0, x: isArabic ? 50 : -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2.5,
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => setSelectedStep(index)}
                    >
                      {/* Number Circle */}
                      <Box
                        component={motion.div}
                        animate={{
                          scale: isSelected ? 1.15 : 1,
                        }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        sx={{
                          bgcolor: isSelected ? step.color : "transparent",
                          color: isSelected ? "#fff" : "#9048FB",
                          width: { xs: 24, md: 28 },
                          height: { xs: 24, md: 28 },
                          borderRadius: "50%",
                          border: isSelected ? "none" : "2px solid #9048FB",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: { xs: "0.8rem", md: "1rem" },
                          fontWeight: "bold",
                          flexShrink: 0,
                          zIndex: 1,
                          transition: "all 0.3s ease",
                          mt: { xs: "14px", md: "18px" },
                          fontFamily: "var(--font-outfit)",
                        }}
                      >
                        {step.number}
                      </Box>

                      {/* Text Box */}
                      <Box
                        sx={{
                          flex: 1,
                          p: { xs: 1.5, md: 2 },
                          borderRadius: "12px",
                          backgroundColor: isSelected
                            ? step.bgColor
                            : "transparent",
                          border: isSelected
                            ? `2px solid ${step.borderColor}`
                            : "2px solid transparent",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? step.bgColor
                              : "rgba(0,0,0,0.03)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: {
                              xs: "1.1rem",
                              sm: "1.2rem",
                              md: "1.3rem",
                            },
                            color: isSelected ? step.color : "#1C2843",
                            transition: "color 0.3s ease",
                            fontFamily: "var(--font-outfit)",
                          }}
                        >
                          {step.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#666",
                            lineHeight: 1.6,
                            fontSize: {
                              xs: "0.9rem",
                              sm: "0.95rem",
                              md: "1rem",
                            },
                            fontFamily: "var(--font-outfit)",
                          }}
                        >
                          {step.description}
                        </Typography>
                      </Box>

                      {/* Vertical Line */}
                      {index !== steps.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: isArabic ? "auto" : { xs: 11, md: 13 },
                            right: isArabic ? { xs: 11, md: 13 } : "auto",
                            top: { xs: 37, md: 52 },
                            width: "2px",
                            height: {
                              xs: "calc(100% - 40px)",
                              md: "calc(100% - 36px)",
                            },
                            bgcolor: "#C6BAB3",
                            zIndex: 0,
                          }}
                        />
                      )}
                    </Box>
                  </motion.div>
                );
              })}
            </Box>

            {/* CTA Button */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{
                mb: 4,
                mt: "7%",
                ml: { xs: 0, md: isArabic ? 0 : "9%" },
                mr: { xs: 0, md: isArabic ? "9%" : 0 },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Link href="/free-trial-of-ai-recruiting-tool" passHref>
                <Button
                  sx={{
                    bgcolor: "#775CE5",
                    color: "#fff",
                    textTransform: "none",
                    px: 3,
                    py: 1.7,
                    height: { xs: "50px", md: "56px" },
                    width: { xs: "100%", sm: "auto" },
                    fontFamily: "var(--font-outfit)",
                    fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
                    fontWeight: "400",
                    borderRadius: "28px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      bgcolor: "#000",
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  {t("fourEasySteps.cta")}
                </Button>
              </Link>
            </Stack>
          </Box>

          {/* Image Area with Sticky Scroll */}
          <Box
            sx={{
              flex: 1,
              position: { xs: "relative", md: "sticky" },
              top: { md: "100px" },
              height: { xs: "250px", sm: "300px", md: "470px" },
              width: "100%",
              maxWidth: { xs: "100%", md: "50%" },
              display: { xs: "none", md: "block" },
              mt: "2%",
            }}
          >
            <motion.div
              key={selectedStep}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
              <Image
                src={steps[selectedStep].image}
                alt={`${steps[selectedStep].imageAlt} preview`}
                fill
                style={{
                  objectFit: "contain",
                  objectPosition: "center",
                }}
                priority
                placeholder="empty"
                unoptimized
              />
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Box>
  );
});

FourEasySteps.displayName = "FourEasySteps";

export default FourEasySteps;
